import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PatientSectionLayout, usePatientSectionPatient } from './PatientSectionLayout';
import { videoConferenceService } from '../../services/video-conference/videoConferenceService';
import { getConsultationTypeLabel } from '../../utils/appointmentUtils';
import { formatDateTime, formatAppointmentTime } from '../../utils/dateUtils';
import { getAppointmentStatusBadgeClass, getAppointmentStatusLabel } from '../../utils/statusUtils';

type AppointmentFilterTab = 'all' | 'upcoming' | 'past' | 'missed_cancelled' | 'reschedule_pending';

const PatientAppointmentsContent: React.FC = () => {
  const navigate = useNavigate();
  const { patientId } = useParams<{ patientId: string }>();
  const patient = usePatientSectionPatient();
  const [appointmentFilterTab, setAppointmentFilterTab] = useState<AppointmentFilterTab>('upcoming');
  const [canJoinStatuses, setCanJoinStatuses] = useState<Map<number, { canJoin: boolean; message?: string }>>(new Map());
  const [loadingCanJoin, setLoadingCanJoin] = useState<Set<number>>(new Set());

  const loadCanJoinStatus = useCallback(async (appointmentId: number) => {
    setLoadingCanJoin((prev) => new Set(prev).add(appointmentId));
    try {
      const res: { can_join: boolean; message?: string } = await videoConferenceService.canJoinCall(appointmentId);
      setCanJoinStatuses((prev) => {
        const next = new Map(prev);
        next.set(appointmentId, { canJoin: res.can_join, message: res.message });
        return next;
      });
    } catch {
      setCanJoinStatuses((prev) => {
        const next = new Map(prev);
        next.set(appointmentId, { canJoin: false, message: 'Unable to check' });
        return next;
      });
    } finally {
      setLoadingCanJoin((prev) => {
        const next = new Set(prev);
        next.delete(appointmentId);
        return next;
      });
    }
  }, []);

  useEffect(() => {
    if (!patient?.appointments?.length) return;
    patient.appointments.forEach((apt) => {
      if (apt.status === 'scheduled' || apt.status === 'confirmed') loadCanJoinStatus(apt.id);
    });
  }, [patient?.appointments, loadCanJoinStatus]);

  const filteredAppointments = useMemo(() => {
    if (!patient?.appointments?.length) return [];
    const list = patient.appointments;
    const now = new Date();
    if (appointmentFilterTab === 'all') return list;
    if (appointmentFilterTab === 'upcoming') {
      return list
        .filter((apt) => {
          if (!apt.appointment_date) return false;
          const aptDate = new Date(apt.appointment_date);
          const durationMin = apt.duration_minutes ?? apt.duration ?? 30;
          const endTime = new Date(aptDate.getTime() + durationMin * 60 * 1000);
          const status = apt.status?.toLowerCase() || '';
          return endTime > now && status !== 'completed' && status !== 'cancelled' && status !== 'missed';
        })
        .sort((a, b) => new Date(a.appointment_date || 0).getTime() - new Date(b.appointment_date || 0).getTime());
    }
    if (appointmentFilterTab === 'past') {
      return list
        .filter((apt) => (apt.status?.toLowerCase() || '') === 'completed')
        .sort((a, b) => new Date(b.appointment_date || 0).getTime() - new Date(a.appointment_date || 0).getTime());
    }
    if (appointmentFilterTab === 'missed_cancelled') {
      return list
        .filter((apt) => {
          const s = apt.status?.toLowerCase() || '';
          return s === 'missed' || s === 'cancelled';
        })
        .sort((a, b) => new Date(b.appointment_date || 0).getTime() - new Date(a.appointment_date || 0).getTime());
    }
    if (appointmentFilterTab === 'reschedule_pending') {
      return list
        .filter((apt) => (apt.status?.toLowerCase() || '') === 'reschedule_pending')
        .sort((a, b) => new Date(a.appointment_date || 0).getTime() - new Date(b.appointment_date || 0).getTime());
    }
    return list;
  }, [patient?.appointments, appointmentFilterTab]);

  const canJoinCall = (appointmentId: number) => canJoinStatuses.get(appointmentId)?.canJoin ?? false;
  const getJoinMessage = (appointmentId: number) => canJoinStatuses.get(appointmentId)?.message;
  const isLoadingJoin = (appointmentId: number) => loadingCanJoin.has(appointmentId);

  const handleCreateAppointment = () => {
    navigate(`/patients/${patientId}?tab=appointments&createAppointment=1`);
  };

  if (!patient) return null;

  return (
    <section className="dashboard-section patient-tabs-section">
        <div className="section-title-row">
          <h2 className="section-title">Appointments</h2>
          <button type="button" className="primary-button create-appointment-btn" onClick={handleCreateAppointment}>
            Create Appointment
          </button>
        </div>
        {patient.appointments.length === 0 ? (
          <div className="no-appointments">No appointments found for this patient.</div>
        ) : (
          <>
            <div className="patient-appointments-filter-tabs">
              {(['all', 'upcoming', 'past', 'missed_cancelled', 'reschedule_pending'] as const).map((tab) => (
                <button
                  key={tab}
                  type="button"
                  className={`patient-appointment-filter-tab ${appointmentFilterTab === tab ? 'active' : ''}`}
                  onClick={() => setAppointmentFilterTab(tab)}
                >
                  {tab === 'all' ? 'All' : tab === 'upcoming' ? 'Upcoming' : tab === 'past' ? 'Past' : tab === 'missed_cancelled' ? 'Missed & Cancelled' : 'Reschedule Pending'}
                </button>
              ))}
            </div>
            <div className="appointments-table-container">
              <table className="appointments-table">
                <thead>
                  <tr>
                    <th>Date & Time</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Duration</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAppointments.length === 0 ? (
                    <tr>
                      <td colSpan={5} style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
                        No appointments match this filter.
                      </td>
                    </tr>
                  ) : (
                    filteredAppointments.map((appointment) => {
                      const canJoin = canJoinCall(appointment.id);
                      const joinMessage = getJoinMessage(appointment.id);
                      const showJoinButton =
                        (appointment.status === 'scheduled' || appointment.status === 'confirmed') &&
                        appointment.appointment_date &&
                        new Date(new Date(appointment.appointment_date).getTime() + (appointment.duration_minutes ?? appointment.duration ?? 30) * 60 * 1000) > new Date();
                      return (
                        <tr key={appointment.id}>
                          <td>
                            <div className="appointment-date-cell">
                              <div className="appointment-date">{formatDateTime(appointment.appointment_date, true, 'short')}</div>
                              <div className="appointment-time">
                                {formatAppointmentTime(appointment.appointment_date, appointment.duration_minutes ?? appointment.duration)}
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className="appointment-type-badge">{getConsultationTypeLabel(appointment.appointment_type)}</span>
                          </td>
                          <td>
                            <span className={getAppointmentStatusBadgeClass(appointment.status, true)}>
                              {getAppointmentStatusLabel(appointment.status)}
                            </span>
                          </td>
                          <td>{(appointment.duration_minutes ?? appointment.duration) ? `${appointment.duration_minutes ?? appointment.duration} min` : 'N/A'}</td>
                          <td>
                            <div className="appointment-actions">
                              {appointment.status?.toLowerCase() === 'completed' ? (
                                <button className="view-details-btn" onClick={() => navigate(`/appointments/${appointment.id}`)}>
                                  View Details
                                </button>
                              ) : (
                                <>
                                  <div className="appointment-actions-row">
                                    <button
                                      className="action-btn-edit"
                                      onClick={() => navigate(`/appointments/${appointment.id}/edit`, { state: { returnToPatientId: patientId } })}
                                    >
                                      Edit
                                    </button>
                                    <button className="view-details-btn" onClick={() => navigate(`/appointments/${appointment.id}`)}>
                                      View
                                    </button>
                                    {showJoinButton && (
                                      <button
                                        className={`action-btn-join ${!canJoin ? 'disabled' : ''}`}
                                        onClick={() => canJoin && navigate(`/video-conference/${appointment.id}`)}
                                        disabled={!canJoin}
                                      >
                                        <span className="action-icon">📹</span>
                                        Join Call
                                      </button>
                                    )}
                                  </div>
                                  {showJoinButton && (joinMessage || isLoadingJoin(appointment.id)) && (
                                    <div className="join-call-container">
                                      {!canJoin && joinMessage && !isLoadingJoin(appointment.id) && (
                                        <div className="join-call-note">{joinMessage}</div>
                                      )}
                                      {isLoadingJoin(appointment.id) && <div className="join-call-note">Checking availability...</div>}
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
    </section>
  );
};

const PatientAppointmentsPage: React.FC = () => (
  <PatientSectionLayout section="appointments">
    <PatientAppointmentsContent />
  </PatientSectionLayout>
);

export default PatientAppointmentsPage;
